import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';

import { Text, Separator, Button } from '@cuemath/comps';

import {
  post as createTeacher,
  invalidateList as invalidateTeacherList,
} from 'actions/teacher-list';
import { patch as editTeacher } from 'actions/teachers';

import {
  Head,
  Footer,
  Message,
  FormWrapper,
  FieldWrapper,
  ButtonWrapper,
  NavigationButtons,
  NavButton,
  renderFormField,
  getOptions,
} from 'modules/form';

import {
  FORM_ACTIONS,
  FIELD_TYPES,
  USER_TAB,
  teacherFields,
  getOnChangeMethod,
  getInputChangeMethod,
  getHeadText,
} from 'constants/form';

class CreateTeacher extends PureComponent {
  static defaultProps = {
    teacher: null,
  };

  constructor(props) {
    super(props);
    const { teacher, formType } = this.props;
    this.state = {
      isValid: true,
      errorText: null,
      message: null,
      teacherPost: store.post(),
      teacherPatch: store.patch(),
      teacherData: teacher,
      tab: USER_TAB.ACCOUNT,
      type: formType,
      form: this.getFormDefaults(),
      formFields: this.getFormFields(),
    };
    teacherFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static propTypes = {
    actions: PropTypes.shape({
      createTeacher: PropTypes.func.isRequired,
      editTeacher: PropTypes.func.isRequired,
      invalidateTeacherList: PropTypes.func.isRequired,
    }).isRequired,
    teacher: PropTypes.object,
    onNavigate: PropTypes.func.isRequired,
    userConfig: PropTypes.object.isRequired,
    programs: PropTypes.array.isRequired,
  };

  getFormDefaults = () => {
    const { teacher } = this.props;
    const formDefaults = {};

    teacherFields.forEach(field => {
      formDefaults[field.name] =
        !!teacher && teacher[field.param] ? teacher[field.param] : field.default;
    });
    return formDefaults;
  };

  getFormFields = () => {
    const { userConfig, programs } = this.props;

    return teacherFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT)
        field.choices = getOptions({ field: field.name, userConfig, programs });
      return field;
    });
  };

  static getDerivedStateFromProps(props, state) {
    const { teacherList, teachers, isLast, actions } = props;
    const { teacherPost, teacherPatch, tab } = state;
    const newTeacherPost = store.post(teacherList, teacherPost.id);
    const newTeacherPatch = store.patch(teachers, teacherPatch.id);
    const newState = {};

    if (newTeacherPost.resolved) {
      if (newTeacherPost.resolved.isCreated) {
        newState.teacherPost = newTeacherPost;
        newState.teacherData = newTeacherPost.resolved.data;
        newState.message = 'Teacher account created successfully';
        if (isLast) newState.type = FORM_ACTIONS.VIEW;
        else newState.tab = tab + 1;
        actions.invalidateTeacherList();
      } else if (newTeacherPost.resolved.isCreatingFailed) {
        newState.teacherPost = newTeacherPost;
        newState.errorText = store.getError(newTeacherPost.resolved).message;
      }
    }
    if (newTeacherPatch.resolved) {
      if (newTeacherPatch.resolved.isPatched) {
        newState.teacherPatch = newTeacherPatch;
        newState.teacherData = newTeacherPatch.resolved.data;
        newState.type = FORM_ACTIONS.VIEW;
        newState.message = 'Teacher details edited successfully';
        actions.invalidateTeacherList();
      } else if (newTeacherPatch.resolved.isPatchingFailed) {
        newState.teacherPatch = newTeacherPatch;
        newState.errorText = store.getError(newTeacherPatch.resolved).message;
      }
      newState.updateTeacher = false;
    }
    return newState;
  }

  componentDidUpdate() {
    const { tab: formTab, formType, onNavigate } = this.props;
    const { tab, type, teacherData, message } = this.state;

    if (tab !== formTab || type !== formType)
      onNavigate({ tab, type, data: teacherData, message });
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;

    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePostAction = () => {
    const {
      teacherPost: { id },
      form,
      formFields,
    } = this.state;
    const { actions } = this.props;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (param) data[param] = form[name];
    });
    actions.createTeacher(id, data);
  };

  handlePatchAction = () => {
    const {
      teacherPatch: { id },
      form,
      formFields,
    } = this.state;
    const { actions, teacher } = this.props;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (param && teacher[param] !== form[name]) data[param] = form[name];
    });

    if (Object.keys(data).length) {
      actions.editTeacher(id, teacher.id, data);
    }
  };

  onSubmit = event => {
    const {
      teacherPost: { current },
    } = this.state;
    const { formType } = this.props;

    event.preventDefault();

    if (formType === FORM_ACTIONS.CREATE && !(current && current.isCreating))
      this.handlePostAction();
    else if (formType === FORM_ACTIONS.EDIT) this.handlePatchAction();
  };

  nextTab = () => {
    const { tab } = this.state;
    this.props.onNavigate({ tab: tab + 1 });
  };

  prevTab = () => {
    const { tab } = this.state;
    this.props.onNavigate({ tab: tab - 1 });
  };

  renderFormHeader = () => {
    const { formType } = this.props;

    return (
      <Head>
        <Text size="xlarge" align="center">
          {getHeadText(formType, 'Teacher')}
        </Text>
      </Head>
    );
  };

  renderFormFields = () => {
    const { formType } = this.props;
    const { formFields, form } = this.state;

    return (
      <FieldWrapper>
        {formFields.map(field => {
          const disabled =
            field.disabled ||
            formType === FORM_ACTIONS.VIEW ||
            (!field.editable && formType === FORM_ACTIONS.EDIT);
          const required = formType === FORM_ACTIONS.CREATE ? field.required : false;
          const placeholder = disabled ? '' : field.label;

          return renderFormField({
            ...field,
            value: form[field.name],
            onChange: this[getInputChangeMethod(field)],
            required,
            disabled,
            placeholder,
          });
        })}
      </FieldWrapper>
    );
  };

  renderFormFooter = () => {
    const { formType, isFirst, isLast } = this.props;
    const { teacherData } = this.state;

    return (
      <Footer>
        <ButtonWrapper>
          <NavigationButtons>
            <NavButton
              label={
                formType === FORM_ACTIONS.EDIT ? 'Skip to Previous' : 'Go to Previous'
              }
              disabled={!teacherData || isFirst}
              onClick={this.prevTab}
            />
          </NavigationButtons>
          <Button label="SAVE" disabled={formType === FORM_ACTIONS.VIEW} />
          <NavigationButtons>
            <NavButton
              label={formType === FORM_ACTIONS.EDIT ? 'Skip to Next' : 'Go to Next'}
              disabled={!teacherData || isLast}
              onClick={this.nextTab}
            />
          </NavigationButtons>
        </ButtonWrapper>
      </Footer>
    );
  };

  render() {
    const { message } = this.props;
    const { errorText } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader()}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter()}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ teacherList, teachers, teacherSlots }) => ({ teacherList, teachers, teacherSlots }),
  dispatch => ({
    actions: bindActionCreators(
      {
        createTeacher,
        editTeacher,
        invalidateTeacherList,
      },
      dispatch,
    ),
  }),
)(CreateTeacher);
